import { computed } from 'vue';
import { UserRepository } from '~/repository/UserRepository';
import type { StationWithOrganization } from '~/types/common/stationWithOrganization';

/**
 * OperationStationModalのロジックをまとめたcomposable
 */
export const useOperationStationModalLogic = (displayId: string) => {
  const userRepository = new UserRepository(displayId);
  const { startLoad, endLoad } = useLoadingStore();
  const loginUserStore = useLoginUserStore();
  const { loginUser } = storeToRefs(loginUserStore);

  // キャンセルボタンを隠すかどうか
  // 作業駅が未設定の場合は、選択するまで閉じさせないために隠す。
  // ただし代理ログイン中（CH・MAX）は未設定なのが代理ログイン先ユーザの作業駅であり、
  // 代理ログインを中断して元のユーザに戻る手段が必要なため隠さない。
  const hideCancel = computed(() =>
    !loginUser.value?.workingStation
    && !loginUser.value?.proxyLoginMode,
  );

  // よく使う駅エリアを表示するかどうか（代理ログイン中は表示・記録しない）
  const showFrequentStations = computed(() => !loginUser.value?.proxyLoginMode);

  // 完了イベントのコールバック
  let completedCallback: (() => void) | null = null;
  // キャンセルイベントのコールバック
  let canceledCallback: (() => void) | null = null;

  /**
   * 選択された情報で作業駅を更新する
   *
   * @param selectedStation 選択された駅情報
   */
  const updateWorkingStation = async (selectedStation: StationWithOrganization): Promise<void> => {
    startLoad();
    try {
      // 作業駅更新APIを呼び出す。
      await userRepository.updateWorkStation({
        stationCd: selectedStation.stationCd,
        stationSubCd: selectedStation.stationSubCd,
      });

      // ログインユーザー情報ストアに選択された作業駅を保存する
      loginUserStore.setWorkingStation(selectedStation);

      if (completedCallback) {
        completedCallback();
      }
    }
    finally {
      endLoad();
    }
  };

  /**
   * キャンセル処理
   */
  const handleCanceled = (): void => {
    if (canceledCallback) {
      canceledCallback();
    }
  };

  /**
   * completedイベントのコールバック関数を設定
   */
  const setCompletedCallback = (callback: () => void): void => {
    completedCallback = callback;
  };

  /**
   * canceledイベントのコールバック関数を設定
   */
  const setCanceledCallback = (callback: () => void): void => {
    canceledCallback = callback;
  };

  return {
    // 状態
    hideCancel,
    showFrequentStations,

    // メソッド
    updateWorkingStation,
    handleCanceled,
    setCompletedCallback,
    setCanceledCallback,
  };
};
